import { FC } from "react";
import { Prompt } from "react-router-dom";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  Button,
  FormControl,
  FormControlLabel,
  Stack,
  Switch,
} from "@mui/material";
import __ from "src/utils/Translations";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import { useSaveFeatureConfiguration } from "src/Pages/Company/Absences/api/featureConfiguration.queries";
import { parseDataToBackend } from "src/utils/Company/Absences/parseFeatureConfiguration";
import { IFormValues } from ".";

interface IAbsencesConfigurationFormProps {
  defaultValues: IFormValues;
}

const AbsencesConfigurationForm: FC<IAbsencesConfigurationFormProps> = ({
  defaultValues,
}) => {
  const companyId = useCompanyId();
  const logStatus = useLogStatus();

  if (!companyId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No company ID provided.",
      file: "AbsencesConfigurationForm.tsx",
    });
  }

  const featureConfigurationMutation = useSaveFeatureConfiguration(companyId);

  const formMethods = useForm<IFormValues>({
    defaultValues,
  });

  const {
    control,
    formState: { isDirty },
    handleSubmit,
  } = formMethods;

  const onSubmit = (formData: IFormValues) => {
    const parsedFormData = parseDataToBackend(formData);

    featureConfigurationMutation.mutate(parsedFormData);
  };

  return (
    <FormProvider {...formMethods}>
      <form
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        data-testid="absences-configuration-form"
      >
        <FormControl component="fieldset" variant="standard" margin="normal">
          <Controller
            name="VISIBLE_FOR_EMPLOYEES"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!value}
                    onChange={onChange}
                    data-testid="absences-configuration-form-field__VISIBLE_FOR_EMPLOYEES"
                  />
                }
                label={__("Moduł nieobecności widoczny")}
                labelPlacement="start"
                sx={{ ml: 0 }}
              />
            )}
          />
        </FormControl>
        <Stack direction="row" justifyContent="flex-end" spacing={2} mt="2rem">
          <Button
            variant="contained"
            type="submit"
            size="large"
            loading={featureConfigurationMutation.isPending}
            loadingPosition="start"
            data-testid="absences-configuration-form__button-submit"
          >
            {__("Zapisz zmiany")}
          </Button>
        </Stack>
      </form>
      <Prompt
        when={isDirty && !featureConfigurationMutation.isSuccess}
        message={__("Niezapisane zmiany zostaną utracone.")}
      />
    </FormProvider>
  );
};

export default AbsencesConfigurationForm;
